import React, { useState, useEffect } from 'react';
import { Card, Row, Col, List, Tag, Empty, Spin, Alert, Typography, Collapse, Divider, Space } from 'antd';
import {
  HistoryOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
  SafetyOutlined,
  AlertOutlined,
  TeamOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { usePatientPortal } from '../context/PatientPortalContext';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const PatientPortalMedicalHistory = () => {
  const { getPortalAxios } = usePatientPortal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [surgicalHistory, setSurgicalHistory] = useState([]);
  const [problems, setProblems] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [familyHistory, setFamilyHistory] = useState([]);
  const [structuredMedicalHistory, setStructuredMedicalHistory] = useState({});
  const [structuredSurgicalHistory, setStructuredSurgicalHistory] = useState({});
  const [structuredFamilyHistory, setStructuredFamilyHistory] = useState({});

  useEffect(() => {
    fetchMedicalHistoryData();
  }, []);

  const fetchMedicalHistoryData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const portalAxios = getPortalAxios();
      
      // Fetch all medical history data in parallel
      const [medicalRes, surgicalRes, problemsRes, allergiesRes, familyRes] = await Promise.all([
        portalAxios.get('/portal/medical-history'),
        portalAxios.get('/portal/surgical-history'),
        portalAxios.get('/portal/problems'),
        portalAxios.get('/portal/allergies'),
        portalAxios.get('/portal/family-history')
      ]);

      setMedicalHistory(medicalRes.data.data.medicalHistory || []);
      setStructuredMedicalHistory(medicalRes.data.data.structuredHistory || {});

      setSurgicalHistory(surgicalRes.data.data.surgicalHistory || []);
      setStructuredSurgicalHistory(surgicalRes.data.data.structuredHistory || {});

      setProblems(problemsRes.data.data.problems || []);
      setAllergies(allergiesRes.data.data.allergies || []);
      setFamilyHistory(familyRes.data.data.familyHistory || []);
      setStructuredFamilyHistory(familyRes.data.data.structuredHistory || []);
      
    } catch (err) {
      console.error('Error fetching medical history:', err);
      setError('Failed to load medical history data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Date not specified';
    return moment(date).format('MMM DD, YYYY');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'chronic':
        return 'red';
      case 'stable':
        return 'orange';
      case 'resolved':
      case 'historical':
        return 'green';
      default:
        return 'blue';
    }
  };

  const renderMedicalHistoryCard = () => (
    <Card
      title={
        <Title level={4} style={{ color: '#34495e', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HistoryOutlined style={{ color: '#3498db' }} />
          📋 Medical History
        </Title>
      }
      style={{ marginBottom: 24, background: '#fdfdfd', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
    >
      <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#e8f4fd', borderRadius: '6px', border: '1px solid #bee5eb' }}>
        <Text style={{ fontSize: '13px', color: '#0c5460' }}>
          <strong>📝 Your Medical History:</strong> Past medical conditions, diagnoses, and health events
        </Text>
      </div>

      {/* Free-text Medical History */}
      {medicalHistory.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Title level={5} style={{ color: '#2c3e50', marginBottom: 12 }}>Past Medical Conditions</Title>
          <List
            dataSource={medicalHistory}
            renderItem={(item) => (
              <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <List.Item.Meta
                  avatar={<HeartOutlined style={{ fontSize: '18px', color: '#e74c3c' }} />}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <Text strong style={{ fontSize: '15px' }}>{item.condition}</Text>
                      <Tag color={getStatusColor(item.status)}>{item.status || 'Historical'}</Tag>
                    </div>
                  }
                  description={
                    <div>
                      {item.diagnosisDate && (
                        <div style={{ marginBottom: '4px' }}>
                          <CalendarOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                          <Text type="secondary">Diagnosed: {formatDate(item.diagnosisDate)}</Text>
                        </div>
                      )}
                      {item.notes && (
                        <div>
                          <FileTextOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                          <Text type="secondary">{item.notes}</Text>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      {/* Structured Medical History */}
      {Object.keys(structuredMedicalHistory).length > 0 && (
        <div>
          <Title level={5} style={{ color: '#2c3e50', marginBottom: 12 }}>Medical History by Category</Title>
          <Collapse size="small" ghost>
            {Object.entries(structuredMedicalHistory).map(([category, conditions]) => (
              <Panel 
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SafetyOutlined style={{ color: '#3498db' }} />
                    <Text strong>{category}</Text>
                    <Tag color="blue">{conditions.length}</Tag>
                  </div>
                } 
                key={category}
              >
                <div style={{ paddingLeft: '24px' }}>
                  {conditions.map((condition, index) => (
                    <Tag key={index} style={{ margin: '2px 4px 2px 0', padding: '4px 8px' }}>
                      {condition}
                    </Tag>
                  ))}
                </div>
              </Panel>
            ))}
          </Collapse>
        </div>
      )}

      {medicalHistory.length === 0 && Object.keys(structuredMedicalHistory).length === 0 && (
        <Empty 
          description="No medical history recorded" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '20px 0' }}
        />
      )}
    </Card>
  );

  const renderSurgicalHistoryCard = () => (
    <Card
      title={
        <Title level={4} style={{ color: '#34495e', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MedicineBoxOutlined style={{ color: '#e67e22' }} />
          🏥 Surgical History
        </Title>
      }
      style={{ marginBottom: 24, background: '#fdfdfd', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
    >
      <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ffcc80' }}>
        <Text style={{ fontSize: '13px', color: '#e65100' }}>
          <strong>🔧 Your Surgical History:</strong> Previous surgeries, procedures, and operations
        </Text>
      </div>

      {/* Free-text Surgical History */}
      {surgicalHistory.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Title level={5} style={{ color: '#2c3e50', marginBottom: 12 }}>Past Surgeries & Procedures</Title>
          <List
            dataSource={surgicalHistory}
            renderItem={(item) => (
              <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <List.Item.Meta
                  avatar={<MedicineBoxOutlined style={{ fontSize: '18px', color: '#e67e22' }} />}
                  title={<Text strong style={{ fontSize: '15px' }}>{item.procedure}</Text>}
                  description={
                    <div>
                      {item.procedureDate && (
                        <div style={{ marginBottom: '4px' }}>
                          <CalendarOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                          <Text type="secondary">Date: {formatDate(item.procedureDate)}</Text>
                        </div>
                      )}
                      {item.surgeon && (
                        <div style={{ marginBottom: '4px' }}>
                          <UserOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                          <Text type="secondary">Surgeon: {item.surgeon}</Text>
                        </div>
                      )}
                      {item.notes && (
                        <div>
                          <FileTextOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                          <Text type="secondary">{item.notes}</Text>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      {/* Structured Surgical History */}
      {Object.keys(structuredSurgicalHistory).length > 0 && (
        <div>
          <Title level={5} style={{ color: '#2c3e50', marginBottom: 12 }}>Surgical History by Category</Title>
          <Collapse size="small" ghost>
            {Object.entries(structuredSurgicalHistory).map(([category, procedures]) => (
              <Panel 
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MedicineBoxOutlined style={{ color: '#e67e22' }} />
                    <Text strong>{category}</Text>
                    <Tag color="orange">{procedures.length}</Tag>
                  </div>
                } 
                key={category}
              >
                <div style={{ paddingLeft: '24px' }}>
                  {procedures.map((procedure, index) => (
                    <Tag key={index} style={{ margin: '2px 4px 2px 0', padding: '4px 8px' }}>
                      {procedure}
                    </Tag>
                  ))}
                </div>
              </Panel>
            ))}
          </Collapse>
        </div>
      )}

      {surgicalHistory.length === 0 && Object.keys(structuredSurgicalHistory).length === 0 && (
        <Empty 
          description="No surgical history recorded" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '20px 0' }}
        />
      )}
    </Card>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" tip="Loading your medical history..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Unable to Load Medical History"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'severe':
      case 'life-threatening':
        return 'red';
      case 'moderate':
        return 'orange';
      case 'mild':
        return 'yellow';
      default:
        return 'blue';
    }
  };

  const renderAllergiesCard = () => (
    <Card
      title={
        <Title level={4} style={{ color: '#34495e', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertOutlined style={{ color: '#f39c12' }} />
          ⚠️ Allergies & Reactions
        </Title>
      }
      style={{ marginBottom: 24, background: '#fdfdfd', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
    >
      <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#fff8e1', borderRadius: '6px', border: '1px solid #ffcc02' }}>
        <Text style={{ fontSize: '13px', color: '#f57c00' }}>
          <strong>🚨 Important Safety Information:</strong> Known allergies and adverse reactions to medications or substances
        </Text>
      </div>

      {allergies.length > 0 ? (
        <List
          dataSource={allergies}
          renderItem={(item) => (
            <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
              <List.Item.Meta
                avatar={<WarningOutlined style={{ fontSize: '18px', color: getSeverityColor(item.severity) }} />}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Text strong style={{ fontSize: '15px' }}>{item.substance}</Text>
                    {item.severity && <Tag color={getSeverityColor(item.severity)}>{item.severity}</Tag>}
                    <Tag color={getStatusColor(item.status)}>{item.status}</Tag>
                  </div>
                }
                description={
                  <div>
                    {item.reaction && (
                      <div style={{ marginBottom: '4px' }}>
                        <ExclamationCircleOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                        <Text type="secondary">Reaction: {item.reaction}</Text>
                      </div>
                    )}
                    {item.notes && (
                      <div>
                        <FileTextOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                        <Text type="secondary">{item.notes}</Text>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty
          description="No known allergies recorded"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '20px 0' }}
        />
      )}
    </Card>
  );

  const renderFamilyHistoryCard = () => (
    <Card
      title={
        <Title level={4} style={{ color: '#34495e', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TeamOutlined style={{ color: '#9b59b6' }} />
          👨‍👩‍👧‍👦 Family History
        </Title>
      }
      style={{ marginBottom: 24, background: '#fdfdfd', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
    >
      <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f3e5f5', borderRadius: '6px', border: '1px solid #ce93d8' }}>
        <Text style={{ fontSize: '13px', color: '#7b1fa2' }}>
          <strong>🧬 Genetic Health Information:</strong> Family medical history that may affect your health risks
        </Text>
      </div>

      {/* Free-text Family History */}
      {familyHistory.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Title level={5} style={{ color: '#2c3e50', marginBottom: 12 }}>Family Medical Conditions</Title>
          <List
            dataSource={familyHistory}
            renderItem={(item) => (
              <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <List.Item.Meta
                  avatar={<TeamOutlined style={{ fontSize: '18px', color: '#9b59b6' }} />}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <Text strong style={{ fontSize: '15px' }}>{item.condition}</Text>
                      <Tag color="purple">{item.relationship}</Tag>
                    </div>
                  }
                  description={
                    item.notes && (
                      <div>
                        <FileTextOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                        <Text type="secondary">{item.notes}</Text>
                      </div>
                    )
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      {/* Structured Family History */}
      {structuredFamilyHistory.length > 0 && (
        <div>
          <Title level={5} style={{ color: '#2c3e50', marginBottom: 12 }}>Family Health Conditions</Title>
          <div style={{ paddingLeft: '24px' }}>
            {structuredFamilyHistory.map((condition, index) => (
              <Tag key={index} style={{ margin: '2px 4px 2px 0', padding: '4px 8px' }}>
                {condition}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {familyHistory.length === 0 && structuredFamilyHistory.length === 0 && (
        <Empty
          description="No family history recorded"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '20px 0' }}
        />
      )}
    </Card>
  );

  const renderActiveProblemListCard = () => (
    <Card
      title={
        <Title level={4} style={{ color: '#34495e', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#e74c3c' }} />
          🚨 Active Problem List
        </Title>
      }
      style={{ marginBottom: 24, background: '#fdfdfd', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
    >
      <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#ffebee', borderRadius: '6px', border: '1px solid #ffcdd2' }}>
        <Text style={{ fontSize: '13px', color: '#c62828' }}>
          <strong>⚠️ Current Health Concerns:</strong> Active medical problems requiring ongoing attention
        </Text>
      </div>

      {problems.length > 0 ? (
        <List
          dataSource={problems}
          renderItem={(item) => (
            <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
              <List.Item.Meta
                avatar={<ExclamationCircleOutlined style={{ fontSize: '18px', color: '#e74c3c' }} />}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Text strong style={{ fontSize: '15px' }}>{item.problemName}</Text>
                    <Tag color={getStatusColor(item.status)}>{item.status}</Tag>
                    {item.severity && <Tag color="volcano">{item.severity}</Tag>}
                  </div>
                }
                description={
                  <div>
                    {item.dateOfOnset && (
                      <div style={{ marginBottom: '4px' }}>
                        <CalendarOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                        <Text type="secondary">Since: {formatDate(item.dateOfOnset)}</Text>
                      </div>
                    )}
                    {item.icdCode && (
                      <div style={{ marginBottom: '4px' }}>
                        <FileTextOutlined style={{ marginRight: '4px', color: '#7f8c8d' }} />
                        <Text type="secondary">Code: {item.icdCode}</Text>
                      </div>
                    )}
                    {item.notes && (
                      <div>
                        <Text type="secondary">{item.notes}</Text>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty
          description="No active problems recorded"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '20px 0' }}
        />
      )}
    </Card>
  );

  return (
    <div className="patient-portal-medical-history">
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          {renderMedicalHistoryCard()}
        </Col>
        <Col xs={24} lg={12}>
          {renderSurgicalHistoryCard()}
        </Col>
        <Col xs={24} lg={12}>
          {renderAllergiesCard()}
        </Col>
        <Col xs={24} lg={12}>
          {renderFamilyHistoryCard()}
        </Col>
        <Col xs={24}>
          {renderActiveProblemListCard()}
        </Col>
      </Row>
    </div>
  );
};

export default PatientPortalMedicalHistory;
